<script setup>
import { computed } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useAlert } from 'dashboard/composables';
import { usePhoneAudioDevices } from 'dashboard/composables/usePhoneAudioDevices';

const emit = defineEmits(['close']);

// Everything below is shared with the inline CTI panel (CtiAudioSettings).
const {
  microphones,
  speakers,
  selectedMicrophone,
  selectedSpeaker,
  microphoneLevel,
  microphoneBand,
  microphoneVerdict,
  microphoneVerdictText,
  speakerVolume,
  isTestingMicrophone,
  isTestingSpeaker,
  isCallActive,
  microphonePermissionDenied,
  microphoneDisconnected,
  speakerDisconnected,
  speakerTestUsedDefault,
  savedDevice,
  permissionErrorMessage,
  microphoneOptionLabel,
  speakerOptionLabel,
  onMicrophoneChange,
  onSpeakerChange,
  testMicrophone,
  testSpeaker,
  cleanup,
} = usePhoneAudioDevices({ onError: message => useAlert(message) });

const VERDICT_ICONS = {
  listening: 'i-lucide-audio-lines',
  silent: 'i-lucide-mic-off',
  low: 'i-lucide-triangle-alert',
  good: 'i-lucide-circle-check',
};

const verdictIcon = computed(() => VERDICT_ICONS[microphoneVerdict.value]);

const close = () => {
  cleanup();
  emit('close');
};
</script>

<template>
  <div class="audio-settings-overlay" @click.self="close">
    <div class="audio-settings-popup">
      <div class="popup-header">
        <h3>{{ $t('PHONE_AUDIO.TITLE') }}</h3>
        <button class="close-btn" @click="close">
          <Icon icon="i-lucide-x" />
        </button>
      </div>

      <div class="popup-content">
        <div class="device-section">
          <div class="section-title">
            <h4>{{ $t('PHONE_AUDIO.MICROPHONE.TITLE') }}</h4>
            <span v-if="savedDevice === 'microphone'" class="saved-badge">
              <Icon icon="i-lucide-check" />
              {{ $t('PHONE_AUDIO.SAVED') }}
            </span>
          </div>
          <select
            v-model="selectedMicrophone"
            class="device-select"
            @change="onMicrophoneChange($event.target.value)"
          >
            <option value="">{{ $t('PHONE_AUDIO.SYSTEM_DEFAULT') }}</option>
            <option
              v-for="mic in microphones"
              :key="mic.deviceId"
              :value="mic.deviceId"
            >
              {{ microphoneOptionLabel(mic) }}
            </option>
          </select>

          <div v-if="microphoneDisconnected" class="device-note">
            <Icon icon="i-lucide-unplug" />
            <span>{{ $t('PHONE_AUDIO.DEVICE_DISCONNECTED') }}</span>
          </div>

          <div v-if="microphonePermissionDenied" class="permission-error">
            <Icon icon="i-lucide-mic-off" />
            <span>{{ permissionErrorMessage }}</span>
          </div>

          <div class="test-section">
            <button
              class="test-btn"
              :class="{ active: isTestingMicrophone }"
              :disabled="isCallActive"
              @click="testMicrophone"
            >
              <Icon
                :icon="
                  isTestingMicrophone ? 'i-lucide-mic-off' : 'i-lucide-mic'
                "
              />
              {{
                isTestingMicrophone
                  ? $t('PHONE_AUDIO.MICROPHONE.STOP_TEST')
                  : $t('PHONE_AUDIO.MICROPHONE.START_TEST')
              }}
            </button>

            <div class="level-meter">
              <div class="level-label">
                {{ $t('PHONE_AUDIO.MICROPHONE.LEVEL') }}
              </div>
              <div
                class="level-bar"
                :class="{
                  'level-bar-silent':
                    isTestingMicrophone && microphoneVerdict === 'silent',
                }"
              >
                <div
                  class="level-fill"
                  :class="`level-fill-${microphoneBand}`"
                  :style="{ width: `${microphoneLevel}%` }"
                />
                <div class="level-threshold" />
              </div>
              <div class="level-value">{{ microphoneLevel }}%</div>
            </div>

            <div
              v-if="isTestingMicrophone"
              class="mic-verdict"
              :class="`mic-verdict-${microphoneVerdict}`"
            >
              <Icon :icon="verdictIcon" />
              <span>
                {{ microphoneVerdictText }}
              </span>
            </div>
          </div>
        </div>

        <div class="device-section">
          <div class="section-title">
            <h4>{{ $t('PHONE_AUDIO.SPEAKER.TITLE') }}</h4>
            <span v-if="savedDevice === 'speaker'" class="saved-badge">
              <Icon icon="i-lucide-check" />
              {{ $t('PHONE_AUDIO.SAVED') }}
            </span>
          </div>
          <select
            v-model="selectedSpeaker"
            class="device-select"
            @change="onSpeakerChange($event.target.value)"
          >
            <option value="">{{ $t('PHONE_AUDIO.SYSTEM_DEFAULT') }}</option>
            <option
              v-for="speaker in speakers"
              :key="speaker.deviceId"
              :value="speaker.deviceId"
            >
              {{ speakerOptionLabel(speaker) }}
            </option>
          </select>

          <div v-if="speakerDisconnected" class="device-note">
            <Icon icon="i-lucide-unplug" />
            <span>{{ $t('PHONE_AUDIO.DEVICE_DISCONNECTED') }}</span>
          </div>

          <div class="test-section">
            <button
              class="test-btn"
              :class="{ active: isTestingSpeaker }"
              @click="testSpeaker"
            >
              <Icon
                :icon="
                  isTestingSpeaker ? 'i-lucide-volume-x' : 'i-lucide-volume-2'
                "
              />
              {{
                isTestingSpeaker
                  ? $t('PHONE_AUDIO.SPEAKER.TESTING')
                  : $t('PHONE_AUDIO.SPEAKER.TEST')
              }}
            </button>

            <div v-if="speakerTestUsedDefault" class="permission-error">
              <Icon icon="i-lucide-volume-x" />
              <span>{{ $t('PHONE_AUDIO.SPEAKER.USING_DEFAULT') }}</span>
            </div>

            <div class="volume-control">
              <div class="volume-label">
                {{ $t('PHONE_AUDIO.SPEAKER.VOLUME') }}
              </div>
              <input
                v-model.number="speakerVolume"
                type="range"
                min="0"
                max="1"
                step="0.1"
                class="volume-slider"
              />
              <div class="volume-value">
                {{ Math.round(speakerVolume * 100) }}%
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="popup-footer">
        <button class="btn-primary" @click="close">
          {{ $t('PHONE_AUDIO.DONE') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.audio-settings-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.audio-settings-popup {
  background: white;
  border-radius: 8px;
  width: 500px;
  max-width: 90vw;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;

  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
  }

  .close-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;

    &:hover {
      background: #f3f4f6;
    }
  }
}

.popup-content {
  padding: 20px;
}

.device-section {
  margin-bottom: 30px;

  &:last-child {
    margin-bottom: 0;
  }

  h4 {
    margin: 0;
    font-size: 16px;
    font-weight: 500;
    color: #374151;
  }
}

.section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 15px;
}

.saved-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 500;
  color: #059669;
}

.device-note {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  margin-bottom: 15px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 6px;
  color: #c2410c;
  font-size: 13px;
  line-height: 1.4;
}

.device-select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  margin-bottom: 15px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
}

.permission-error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  margin-bottom: 15px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #b91c1c;
  font-size: 13px;
  line-height: 1.4;
}

.test-section {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.test-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    background: #e5e7eb;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.active {
    background: #dc2626;
    color: white;
    border-color: #dc2626;

    &:hover {
      background: #b91c1c;
    }
  }
}

.level-meter {
  display: flex;
  align-items: center;
  gap: 10px;

  .level-label {
    font-size: 14px;
    color: #6b7280;
    min-width: 60px;
  }

  .level-bar {
    position: relative;
    flex: 1;
    height: 8px;
    background: #e5e7eb;
    border-radius: 4px;
    overflow: hidden;

    // Nothing at all is getting through — an empty bar has no colour of its
    // own, so the track itself turns red.
    &.level-bar-silent {
      background: #fecaca;
    }

    .level-fill {
      height: 100%;
      transition:
        width 0.1s ease,
        background-color 0.2s ease;
    }

    // Bands match MIC_LOW_MAX_PERCENT in dashboard/helper/micLevel.js.
    .level-fill-silent {
      background: #ef4444;
    }

    .level-fill-low {
      background: #f97316;
    }

    .level-fill-good {
      background: #10b981;
    }

    // Where "low" ends and "good" begins.
    .level-threshold {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 40%;
      width: 2px;
      background: rgba(255, 255, 255, 0.8);
    }
  }

  .level-value {
    font-size: 12px;
    color: #6b7280;
    min-width: 40px;
    text-align: right;
  }
}

.mic-verdict {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;

  &.mic-verdict-listening {
    color: #6b7280;
  }

  &.mic-verdict-silent {
    color: #dc2626;
  }

  &.mic-verdict-low {
    color: #ea580c;
  }

  &.mic-verdict-good {
    color: #059669;
  }
}

.volume-control {
  display: flex;
  align-items: center;
  gap: 10px;

  .volume-label {
    font-size: 14px;
    color: #6b7280;
    min-width: 60px;
  }

  .volume-slider {
    flex: 1;
    height: 6px;
    background: #e5e7eb;
    border-radius: 3px;
    outline: none;
    appearance: none;

    &::-webkit-slider-thumb {
      appearance: none;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: #3b82f6;
      cursor: pointer;
    }

    &::-moz-range-thumb {
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: #3b82f6;
      cursor: pointer;
      border: none;
    }
  }

  .volume-value {
    font-size: 12px;
    color: #6b7280;
    min-width: 40px;
    text-align: right;
  }
}

.popup-footer {
  padding: 20px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
}

.btn-primary {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;

  &:hover {
    background: #2563eb;
  }
}

.dark {
  .audio-settings-popup {
    background: #1f2937;
    color: #f9fafb;
  }

  .popup-header {
    border-bottom-color: #374151;
  }

  .popup-footer {
    border-top-color: #374151;
  }

  .device-select {
    background: #374151;
    border-color: #4b5563;
    color: #f9fafb;
  }

  .test-btn {
    background: #374151;
    border-color: #4b5563;
    color: #f9fafb;

    &:hover {
      background: #4b5563;
    }
  }

  .device-note {
    background: rgba(234, 88, 12, 0.15);
    border-color: #7c2d12;
    color: #fdba74;
  }

  .level-bar {
    background: #374151;

    &.level-bar-silent {
      background: rgba(239, 68, 68, 0.35);
    }
  }

  .permission-error {
    background: rgba(220, 38, 38, 0.15);
    border-color: #7f1d1d;
    color: #fca5a5;
  }

  .close-btn:hover {
    background: #374151;
  }
}
</style>
